import type { RegistroAuditoria } from "@/shared/api";
import { t } from "@/shared/i18n";
import { fechaHora, ZONA_CHILE } from "@/shared/lib/fechas";
import { Boton, Tabla, Vacio, type Columna } from "@/shared/ui";
import { describir, type CorreoDe } from "../model/descripcion";
import estilos from "./Registros.module.css";

const a = t.auditoria;

/** «14:05:09» en hora de Chile. */
export function hora(iso: string): string {
  return new Intl.DateTimeFormat("es-CL", {
    timeZone: ZONA_CHILE,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}

/**
 * Registros de la auditoría. En el historial de un usuario el «quién» casi siempre es él o BVL y se
 * muestra la fecha completa; en el registro del día basta la hora.
 */
export function TablaRegistros({
  registros,
  correoDe,
  soloHora = false,
  vacio,
}: {
  readonly registros: readonly RegistroAuditoria[];
  readonly correoDe: CorreoDe;
  readonly soloHora?: boolean;
  readonly vacio: string;
}) {
  if (registros.length === 0) return <Vacio titulo={vacio} />;
  const filas = registros.map((r, i) => ({ r, clave: `${r.momento}-${String(i)}`, l: describir(r, correoDe) }));
  type Fila = (typeof filas)[number];
  const columnas: Columna<Fila>[] = [
    {
      clave: "hora",
      titulo: a.columnaHora,
      orden: ({ r }) => r.momento,
      celda: ({ r }) => (
        <time dateTime={r.momento} className={estilos.hora}>
          {soloHora ? hora(r.momento) : fechaHora(r.momento)}
        </time>
      ),
    },
    {
      clave: "quien",
      titulo: a.columnaQuien,
      rotuloMovil: a.columnaQuien,
      celda: ({ l }) => l.quien,
      orden: ({ l }) => l.quien,
    },
    {
      clave: "que",
      titulo: a.columnaQue,
      rotuloMovil: a.columnaQue,
      celda: ({ r, l }) => <span className={r.tipo === "admin" ? estilos.accion : undefined}>{l.que}</span>,
      orden: ({ l }) => l.que,
    },
    {
      clave: "sobre",
      titulo: a.columnaSobre,
      rotuloMovil: a.columnaSobre,
      celda: ({ l }) => l.sobre,
      orden: ({ l }) => l.sobre,
    },
    {
      clave: "detalle",
      titulo: a.columnaDetalle,
      rotuloMovil: a.columnaDetalle,
      celda: ({ l }) => <span className={estilos.detalle}>{l.detalle}</span>,
      orden: ({ l }) => l.detalle,
    },
  ];
  return <Tabla columnas={columnas} filas={filas} clave={(f) => f.clave} textosOrden={t.tabla} />;
}

export function CargarMas({
  hayMas,
  cargando,
  alCargar,
}: {
  readonly hayMas: boolean;
  readonly cargando: boolean;
  readonly alCargar: () => void;
}) {
  if (!hayMas) return null;
  return (
    <div className={estilos.mas}>
      <Boton disabled={cargando} onClick={alCargar}>
        {cargando ? t.general.cargando : t.general.cargarMas}
      </Boton>
    </div>
  );
}
